import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { RotateCw, Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STAT = ({ label, value, hint, testid, tone = "neutral" }) => (
  <Card><CardContent className="p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${
      tone === "good" ? "text-emerald-700" :
      tone === "bad" ? "text-rose-700" :
      tone === "warn" ? "text-amber-700" : "text-slate-900"
    }`}>{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </CardContent></Card>
);

export function DeliveryCentrePanel() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [queue, setQueue] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await apiClient.get(`/v2_4/delivery-centre/summary`);
        if (alive) setSummary(s.data);
        const r = await apiClient.get(`/v2_4/delivery-centre/recent?limit=50`);
        if (alive) setRecent(r.data?.rows || []);
        const q = await apiClient.get(`/v2_4/delivery-centre/retry-queue`);
        if (alive) setQueue(q.data?.rows || []);
      } catch (e) { /* */ }
    })();
    return () => { alive = false; };
  }, [tick]);

  const retryOne = async (id) => {
    try {
      await apiClient.post(`/v2_4/delivery-centre/retry/${id}`);
      toast.success("Retry queued");
      setTick(t => t + 1);
    } catch (e) { toast.error("Retry failed"); }
  };
  const retryAll = async () => {
    try {
      const r = await apiClient.post(`/v2_4/delivery-centre/retry-all`);
      toast.success(`Triggered ${r.data?.triggered || 0} retries`);
      setTick(t => t + 1);
    } catch (e) { toast.error("Bulk retry failed"); }
  };

  const period = summary?.last7d || {};
  const total24 = (summary?.last24h?.succeeded || 0) + (summary?.last24h?.failed || 0);
  const rate24 = total24 ? Math.round((summary.last24h.succeeded * 100) / total24) : 0;

  return (
    <div className="space-y-5" data-testid="delivery-centre-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <STAT label="Queued (7d)" value={period.queued || 0} testid="dc-queued" />
        <STAT label="Succeeded (7d)" value={period.succeeded || 0}
              testid="dc-succeeded" tone="good" />
        <STAT label="Failed (7d)" value={period.failed || 0}
              testid="dc-failed" tone={period.failed ? "bad" : "neutral"} />
        <STAT label="Retried (7d)" value={period.retried || 0} testid="dc-retried" />
        <STAT label="Success Rate 24h" value={`${rate24}%`}
              testid="dc-success-rate"
              tone={rate24 >= 95 ? "good" : rate24 >= 80 ? "warn" : "bad"} />
      </div>

      {/* By method */}
      {summary?.byMethodLast30d && (
        <Card><CardContent className="p-4">
          <h4 className="font-semibold mb-2 text-sm">By Method (30d)</h4>
          <div className="space-y-1 text-sm" data-testid="dc-by-method">
            {Object.entries(summary.byMethodLast30d).map(([m, vals]) => (
              <div key={m} className="flex items-center justify-between bg-slate-50 rounded-lg p-2"
                   data-testid={`dc-method-${m}`}>
                <span className="font-medium capitalize">{m}</span>
                <div className="flex gap-3 text-xs">
                  <span className="text-emerald-700">✓ {vals.succeeded}</span>
                  <span className="text-rose-700">✗ {vals.failed}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Retry queue */}
      <Card><CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Retry Queue ({queue.length})
          </h4>
          {queue.length > 0 && (
            <Button size="sm" onClick={retryAll} data-testid="dc-retry-all"
                    className="bg-slate-900 hover:bg-slate-800">
              <RotateCw className="h-3.5 w-3.5 mr-1" /> Retry All
            </Button>
          )}
        </div>
        {queue.length === 0 ? (
          <div className="text-sm text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4" /> No failed deliveries
          </div>
        ) : (
          <div className="space-y-1" data-testid="dc-retry-list">
            {queue.slice(0, 25).map(q => (
              <div key={q.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-2 text-xs"
                   data-testid={`dc-retry-row-${q.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">
                    {q.firstName} {q.lastName} · {q.email}
                  </div>
                  <div className="text-slate-500">
                    Attempts: {q.deliveryAttempts || 0} ·{" "}
                    {q.deliveryResponse ? String(q.deliveryResponse).slice(0, 80) : ""}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => retryOne(q.id)}
                        data-testid={`dc-retry-${q.id}`}>
                  <Send className="h-3 w-3 mr-1" /> Retry
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      {/* Recent deliveries */}
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="dc-recent-table">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="py-2 px-3">Donor</th>
              <th className="py-2 px-3">Campaign</th>
              <th className="py-2 px-3">Method</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Attempts</th>
              <th className="py-2 px-3">Last Attempt</th>
            </tr>
          </thead>
          <tbody>
            {recent.map(r => (
              <tr key={r.conversionId} className="border-b hover:bg-slate-50"
                  data-testid={`dc-recent-row-${r.conversionId}`}>
                <td className="py-2 px-3">
                  <div className="font-medium">{r.donorName || "—"}</div>
                  <div className="text-xs text-slate-500">{r.donorEmail || ""}</div>
                </td>
                <td className="py-2 px-3 text-xs">{r.campaignName || "—"}</td>
                <td className="py-2 px-3 text-xs">{r.method || "—"}</td>
                <td className="py-2 px-3">
                  <Badge variant="outline"
                    className={
                      r.status === "sent" ? "bg-emerald-50 text-emerald-700" :
                      r.status === "failed" ? "bg-rose-50 text-rose-700" :
                      "bg-slate-50 text-slate-700"}>
                    {r.status || "—"}
                  </Badge>
                </td>
                <td className="py-2 px-3 text-xs">{r.attempts || 0}</td>
                <td className="py-2 px-3 text-xs">
                  {r.lastAttemptAt ? String(r.lastAttemptAt).slice(0, 16).replace("T", " ") : "—"}
                </td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr><td colSpan="6" className="py-6 text-center text-sm text-slate-500">
                No delivery records yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}


export function OpsHealthPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiClient.get(`/v2_4/ops-health`);
        if (alive) setData(r.data);
      } catch (e) { /* */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="ops-health-panel">
      {/* Stripe */}
      <div>
        <h3 className="font-heading font-bold mb-3">Stripe Health (7d)</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <STAT label="Successful Payments" value={data.stripe?.last7d?.successfulPayments || 0}
                testid="oh-stripe-success" tone="good" />
          <STAT label="Failed Payments" value={data.stripe?.last7d?.failedPayments || 0}
                testid="oh-stripe-failed"
                tone={data.stripe?.last7d?.failedPayments ? "bad" : "neutral"} />
          <STAT label="Active Mandates" value={data.activeMandates || 0}
                testid="oh-mandates-active" />
          <STAT label="Cancelled Mandates" value={data.cancelledMandates || 0}
                testid="oh-mandates-cancelled" />
        </div>
      </div>

      {/* Email */}
      <div>
        <h3 className="font-heading font-bold mb-3">Email Health (7d)</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <STAT label="Invite Emails" value={data.email?.last7d?.inviteEmails || 0}
                testid="oh-email-invite" />
          <STAT label="Invite Sent (Resend)" value={data.email?.last7d?.inviteEmailsSent || 0}
                testid="oh-email-invite-sent" tone="good" />
          <STAT label="Password Resets" value={data.email?.last7d?.passwordResetEmails || 0}
                testid="oh-email-pwreset" />
          <STAT label="Operational" value={data.email?.last7d?.operationalEmails || 0}
                testid="oh-email-ops" />
        </div>
      </div>

      {/* Delivery */}
      <div>
        <h3 className="font-heading font-bold mb-3">Donor Delivery Health (7d)</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <STAT label="Queued" value={data.delivery?.last7d?.queued || 0} testid="oh-d-queued" />
          <STAT label="Succeeded" value={data.delivery?.last7d?.succeeded || 0}
                tone="good" testid="oh-d-succ" />
          <STAT label="Failed" value={data.delivery?.last7d?.failed || 0}
                testid="oh-d-failed"
                tone={data.delivery?.last7d?.failed ? "bad" : "neutral"} />
          <STAT label="Retried" value={data.delivery?.last7d?.retried || 0}
                testid="oh-d-retried" />
          <STAT label="Success %" value={`${data.delivery?.last7d?.successRatePct || 0}%`}
                tone={(data.delivery?.last7d?.successRatePct || 0) >= 95 ? "good" :
                       (data.delivery?.last7d?.successRatePct || 0) >= 80 ? "warn" : "bad"}
                testid="oh-d-rate" />
        </div>
      </div>

      {/* Workforce */}
      <div>
        <h3 className="font-heading font-bold mb-3">Workforce Health</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <STAT label="Total" value={data.workforce?.totalFundraisers || 0} testid="oh-wf-total" />
          <STAT label="Ready" value={data.workforce?.byStatus?.ready || 0}
                tone="good" testid="oh-wf-ready" />
          <STAT label="Nearly Ready" value={data.workforce?.byStatus?.nearly_ready || 0}
                tone="warn" testid="oh-wf-nearly" />
          <STAT label="Action Required" value={data.workforce?.byStatus?.action_required || 0}
                testid="oh-wf-action" tone="warn" />
          <STAT label="Avg Score" value={data.workforce?.avgPassportScore || 0}
                testid="oh-wf-score" />
        </div>
      </div>

      {/* Gift Aid */}
      <div>
        <h3 className="font-heading font-bold mb-3">Gift Aid Health</h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <STAT label="Active Declarations" value={data.giftAid?.activeDeclarations || 0}
                testid="oh-ga-decls" />
          <STAT label="Total Recoverable" value={`£${(data.giftAid?.totalRecoverableAcrossBatches || 0).toFixed(0)}`}
                testid="oh-ga-recoverable" />
          <STAT label="Paid" value={`£${(data.giftAid?.totalPaidAcrossBatches || 0).toFixed(0)}`}
                testid="oh-ga-paid" tone="good" />
        </div>
      </div>

      {/* Platform */}
      <div>
        <h3 className="font-heading font-bold mb-3">Platform Health</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <STAT label="Organisations" value={data.platform?.totalOrganisations || 0}
                testid="oh-pl-orgs" />
          <STAT label="Campaigns" value={data.platform?.activeCampaigns || 0}
                testid="oh-pl-camps" />
          <STAT label="Fundraisers" value={data.platform?.activeFundraisers || 0}
                testid="oh-pl-frs" />
          <STAT label="Donors" value={data.platform?.activeDonors || 0}
                testid="oh-pl-donors" />
          <STAT label="Events (24h)" value={data.platform?.eventsLast24h || 0}
                testid="oh-pl-events" />
          <STAT label="System Version" value={data.platform?.systemVersion || "—"}
                testid="oh-pl-version" />
          <STAT label="Last Backup" value={data.platform?.lastBackupAt || "Not configured"}
                testid="oh-pl-backup" />
        </div>
      </div>
    </div>
  );
}


export function DonorDeliveryHistory({ donorId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!donorId) return;
    let alive = true;
    (async () => {
      try {
        const r = await apiClient.get(`/v2_4/donors/${donorId}/delivery-history`);
        if (alive) setData(r.data);
      } catch (e) { /* */ }
    })();
    return () => { alive = false; };
  }, [donorId]);

  if (!data) return null;
  const s = data.summary || {};
  return (
    <div data-testid="donor-delivery-history">
      <h4 className="font-semibold text-sm mb-2 mt-4">Delivery History</h4>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <STAT label="Delivered" value={s.delivered || 0} tone="good" testid="ddh-delivered" />
        <STAT label="Failed" value={s.failed || 0} tone={s.failed ? "bad" : "neutral"} testid="ddh-failed" />
        <STAT label="Retried" value={s.retried || 0} testid="ddh-retried" />
        <STAT label="Latest Status" value={s.latestStatus || "—"}
              hint={s.latestDestination || ""} testid="ddh-latest" />
      </div>
      <div className="space-y-1 max-h-64 overflow-auto">
        {(data.timeline || []).slice(0, 30).map((e, i) => (
          <div key={i} className="flex justify-between bg-slate-50 rounded px-2 py-1.5 text-xs"
               data-testid={`ddh-event-${i}`}>
            <span className="font-medium">{e.type}</span>
            <span className="text-slate-500">
              {e.method ? `${e.method} · ` : ""}{String(e.createdAt || "").slice(0, 16).replace("T", " ")}
            </span>
          </div>
        ))}
        {(data.timeline || []).length === 0 && (
          <div className="text-xs text-slate-500 text-center py-3">
            No delivery events recorded for this donor.
          </div>
        )}
      </div>
    </div>
  );
}
